package com.schoolconnect.app.repository.parentevents

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.ParentEventListItemResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.feature.common.PageRequest
import com.schoolconnect.app.feature.common.PaginationPolicy
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import com.schoolconnect.app.repository.common.RepositoryCache
import com.schoolconnect.app.repository.common.tenantCacheKey
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope

data class ParentCalendarEvent(
    val id: String,
    val date: String,
    val title: String,
    val eventType: String,
    val status: String,
    val createdOn: String? = null,
    val createdBy: String? = null,
    val lastUpdatedOn: String? = null,
    val lastUpdatedBy: String? = null
)

data class ParentCalendarEventDetail(
    val date: String? = null,
    val title: String? = null,
    val eventType: String? = null,
    val status: String? = null,
    val createdOn: String? = null,
    val createdBy: String? = null,
    val lastUpdatedOn: String? = null,
    val lastUpdatedBy: String? = null
)

data class ParentEventsPageResult(
    val items: List<ParentEventListItemResponse>,
    val totalCount: Int
)

object ParentEventsRepository {
    private val cache = RepositoryCache<JsonObject>()

    suspend fun loadPage(
        query: String?,
        status: String?,
        publishedFrom: String?,
        publishedTo: String?,
        pageIndex: Int,
        pageSize: Int,
        sortBy: String,
        order: String
    ): Result<ParentEventsPageResult> {
        val page = PageRequest(pageIndex, pageSize.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()))
        val normalizedQuery = query?.trim()?.takeIf { it.isNotBlank() }
        val normalizedStatus = status?.trim()?.uppercase()?.takeIf { it.isNotBlank() }
        val normalizedSort = sortBy.trim().ifBlank { "created_at" }
        val normalizedOrder = order.trim().lowercase().takeIf { it == "asc" || it == "desc" } ?: "desc"

        scLog("[DATA_RETRIEVE_SC] ParentEventsRepository loadPage start hasQuery=${normalizedQuery != null} status=${normalizedStatus ?: "<any>"} pageIndex=${page.safePageIndex} pageSize=${page.safePageSize} sortBy=$normalizedSort order=$normalizedOrder")
        return KtorApiClient.getParentEventsPage(
            query = normalizedQuery,
            status = normalizedStatus,
            publishedFrom = publishedFrom?.trim()?.takeIf { it.isNotBlank() },
            publishedTo = publishedTo?.trim()?.takeIf { it.isNotBlank() },
            limit = page.safePageSize,
            offset = page.offset,
            sortBy = normalizedSort,
            order = normalizedOrder
        ).mapCatching { items ->
            val totalCount = items.serverTotalCountOrNull()
                ?: throw IllegalStateException("Parent events response did not include the filtered count.")
            ParentEventsPageResult(items, totalCount)
        }.onSuccess { result ->
            scLog("[DATA_RETRIEVE_SC] ParentEventsRepository loadPage success pageIndex=${page.safePageIndex} count=${result.items.size} total=${result.totalCount}")
        }.onFailure { error ->
            scLog("[DATA_RETRIEVE_SC] ParentEventsRepository loadPage failure pageIndex=${page.safePageIndex}", error)
        }
    }

    suspend fun load(studentId: String?, limit: Int, refreshScope: CoroutineScope? = null): Result<JsonObject> {
        val normalizedStudentId = studentId?.trim()?.takeIf { it.isNotBlank() }
        val key = tenantCacheKey("parent-events:${normalizedStudentId.orEmpty()}:$limit")
        return refreshScope?.let { scope -> cache.load(scope, "[DATA_RETRIEVE_SC] ParentEventsRepository", "load", key) { KtorApiClient.getParentEvents(normalizedStudentId, limit.coerceIn(1, 100)) } }
            ?: KtorApiClient.getParentEvents(normalizedStudentId, limit.coerceIn(1, 100))
    }
    suspend fun loadDetail(eventId: String): Result<JsonObject> = KtorApiClient.getParentEventById(eventId.trim())

    suspend fun loadForCalendar(limit: Int = 100, refreshScope: CoroutineScope? = null): Result<List<ParentCalendarEvent>> =
        load(null, limit, refreshScope).map { response ->
            response.firstArray("events", "items").mapNotNull { element ->
                val event = element.jsonObject
                val id = event.firstString("id", "event_id") ?: return@mapNotNull null
                ParentCalendarEvent(
                    id = id,
                    date = event.firstString("event_date", "expiry_date") ?: return@mapNotNull null,
                    title = event.firstString("title", "name") ?: "Untitled event",
                    eventType = event.firstString("event_type", "type", "applicable_to") ?: "Event",
                    status = event.firstString("status") ?: "PUBLISHED",
                    createdOn = event.firstString("created_at", "published_on", "published_at"),
                    createdBy = event.firstString("created_by", "published_by", "published_by_name"),
                    lastUpdatedOn = event.firstString("updated_at", "last_updated_at"),
                    lastUpdatedBy = event.firstString("updated_by", "last_updated_by")
                )
            }
        }

    suspend fun loadCalendarDetail(eventId: String): Result<ParentCalendarEventDetail> =
        loadDetail(eventId).map { event ->
            val createdOn = event.firstString("created_at", "published_on", "published_at")
            val createdBy = event.firstString("created_by", "published_by", "published_by_name")
            ParentCalendarEventDetail(
                date = event.firstString("event_date", "expiry_date"),
                title = event.firstString("title", "name"),
                eventType = event.firstString("event_type", "type", "applicable_to"),
                status = event.firstString("status"),
                createdOn = createdOn,
                createdBy = createdBy,
                lastUpdatedOn = event.firstString("updated_at", "last_updated_at") ?: createdOn,
                lastUpdatedBy = event.firstString("updated_by", "last_updated_by") ?: createdBy
            )
        }
}

private fun JsonObject.firstString(vararg keys: String): String? =
    keys.firstNotNullOfOrNull { key -> get(key)?.jsonPrimitive?.contentOrNull?.takeIf(String::isNotBlank) }

private fun JsonObject.firstArray(vararg keys: String) =
    keys.firstNotNullOfOrNull { key -> get(key)?.jsonArray }.orEmpty()
